import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Descriptions, Table, Space, Typography, message, Alert } from 'antd';
import CopyInfoButton from '../../../components/CopyInfoButton';
import type { ColumnsType } from 'antd/es/table';
import { getCurrentUserProjects } from '../../../services/project';
import { getWarehouseList, getWarehouseDetail } from '../../../services/warehouse';
import type { Project } from '../../../types/project';
import type { WarehouseDetail, WarehouseListParams } from '../../../types/warehouse';
import type { ListResponse } from '../../../types/common';

const { Title } = Typography;

const ProjectAddress: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [project, setProject] = useState<Project | null>(null);
  const [warehouses, setWarehouses] = useState<WarehouseDetail[]>([]);

  // 生成项目专属出入库识别码
  const generateProjectCode = (projectCode: string) => {
    return `AF-${projectCode}-PRJ`;
  };

  // 获取项目详情（客户无详情权限时，改为通过“当前用户项目列表”匹配）
  const fetchProjectDetail = async () => {
    if (!id) return;
    try {
      const pid = parseInt(id, 10);
      const list = await getCurrentUserProjects();
      const found = Array.isArray(list) ? list.find(p => (p as any)?.id === pid) : undefined;
      if (found) {
        setProject(found as Project);
      } else {
        message.error('未找到项目或无访问权限');
      }
    } catch (error) {
      message.error('获取项目信息失败');
      console.error('获取项目信息失败:', error);
    }
  };

  // 获取仓库列表（仅使用列表返回的数据，避免逐条详情 403）
  const fetchWarehouses = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const listResponse = await getWarehouseList({ page_size: 100 });
      const items = (listResponse.data?.results || []) as WarehouseDetail[];
      // 尝试补全公司名称与详细地址（部分列表接口可能不返回完整字段）
      const enriched = await Promise.all(
        items.map(async (w) => {
          try {
            const detail = await getWarehouseDetail(w.id);
            const data = (detail as any)?.data || detail;
            return { ...w, ...data } as WarehouseDetail;
          } catch (e) {
            // 没有详情权限或出错时，使用列表返回的基础字段
            return w;
          }
        })
      );
      setWarehouses(enriched);
    } catch (error) {
      message.error('获取仓库信息失败');
      console.error('获取仓库信息失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectDetail();
    fetchWarehouses();
  }, [id]);

  // 仓库列表列定义
  const columns: ColumnsType<WarehouseDetail> = [
    {
      title: '仓库名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '收/发件人',
      key: 'recipient',
      render: () => project ? generateProjectCode(project.project_code) : '-',
    },
    {
      title: '公司名称',
      dataIndex: 'company_name',
      key: 'company_name',
      render: (text: any, record) => (record && record.company_name ? record.company_name : '-')
    },
    {
      title: '详细地址',
      key: 'address',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          {record.address_line1 && <div>{record.address_line1}</div>}
          {record.address_line2 && <div>{record.address_line2}</div>}
          <div>
            {[
              record.city || undefined,
              [record.state, record.postal_code].filter(Boolean).join(' ')
            ].filter(Boolean).join(', ') || '-'}
          </div>
          <div>{record.country_display}</div>
        </Space>
      ),
    },
    {
      title: '仓库联系人',
      dataIndex: 'contact_name',
      key: 'contact_name',
    },
    {
      title: '联系电话',
      dataIndex: 'contact_phone',
      key: 'contact_phone',
    },
  ];

  if (!project) {
    return null;
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 上部分：项目信息 */}
      <Card>
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Title level={4} style={{ margin: 0 }}>项目信息</Title>
          <CopyInfoButton
            onClick={async () => {
              try {
                const lines: string[] = [];
                // 项目信息
                lines.push('【项目信息】');
                lines.push(`项目编码: ${project.project_code || ''}`);
                lines.push(`项目名称: ${project.name || ''}`);
                lines.push(`项目专属出入库识别码: ${generateProjectCode(project.project_code)}`);
                lines.push('');
                // 仓库信息
                lines.push('【仓库地址信息】');
                warehouses.forEach((w, idx) => {
                  lines.push(`--- 仓库 ${idx + 1} ---`);
                  lines.push(`仓库名称: ${w.name || ''}`);
                  lines.push(`收/发件人: ${generateProjectCode(project.project_code)}`);
                  lines.push(`公司名称: ${w.company_name || ''}`);
                  if (w.address_line1) lines.push(`地址行1: ${w.address_line1}`);
                  if (w.address_line2) lines.push(`地址行2: ${w.address_line2}`);
                  const cityStateZip = [w.city || undefined, [w.state, w.postal_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');
                  if (cityStateZip) lines.push(`城市/州/邮编: ${cityStateZip}`);
                  lines.push(`国家: ${w.country_display || ''}`);
                  lines.push(`联系人: ${w.contact_name || ''}`);
                  lines.push(`联系电话: ${w.contact_phone || ''}`);
                });

                const text = lines.join('\n');
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  await navigator.clipboard.writeText(text);
                } else {
                  const ta = document.createElement('textarea');
                  ta.value = text;
                  ta.style.position = 'fixed';
                  ta.style.opacity = '0';
                  document.body.appendChild(ta);
                  ta.select();
                  document.execCommand('copy');
                  document.body.removeChild(ta);
                }
                message.success('页面信息已复制到剪贴板');
              } catch (e) {
                message.error('复制失败，请重试');
              }
            }}
          />
        </Space>
        <Descriptions bordered>
          <Descriptions.Item label="项目编码">
            {project.project_code}
          </Descriptions.Item>
          <Descriptions.Item label="项目名称">
            {project.name}
          </Descriptions.Item>
          <Descriptions.Item label="项目专属出入库识别码">
            {generateProjectCode(project.project_code)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 下部分：仓库地址信息 */}
      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space align="center">
            <Title level={4} style={{ margin: 0 }}>项目专属出入库地址信息</Title>
            <Alert
              message={
                <Space>
                  <span style={{ fontWeight: 'bold' }}>重要提示：</span>
                  <span>收/发件人名称和格式不要做任何修改，否则可能影响正常出入库！</span>
                </Space>
              }
              type="warning"
              showIcon
              style={{ 
                marginLeft: 16,
                padding: '4px 12px',
                lineHeight: '24px',
                height: '32px'
              }}
            />
          </Space>
          <Table
            columns={columns}
            dataSource={warehouses}
            rowKey="id"
            loading={loading}
            pagination={false}
          />
        </Space>
      </Card>
    </Space>
  );
};

export default ProjectAddress; 